import { ChapterCard } from "@/components/ChapterCard";
import { Card, CardContent } from "@/components/ui/card";
import { ComponentProps, forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, LoaderCircle } from "lucide-react";
import { supabase } from "@/lib/supabase/client";
import { ChapterEntry, ChapterId, StepEntry, StepId } from "@/types/content-types";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useUserContext } from "@/contexts/user";
import { useNavigationContext } from "@/contexts/navigation";
import { config } from "@/config";
import { useUnsavedChanges as useUnsavedChanges } from "@/contexts/unsavedChanges";
import { StepHandlerProvider } from "@/contexts/stepHandler/provider";
import { AppContentProvider } from "@/contexts/appContent/AppContentProvider";
import { AppContentAPIContext } from "@/contexts/appContent/appContentHooks";
import { ChapterHandlerProvider } from "@/contexts/chapterHandler/provider";
import { useSyncHeight } from "@/hooks/useMaxHeight";
import { useSwipeIndex } from "@/hooks/useSwipeIndex";
import { cn } from "@/lib/utils";
import { AudioButton } from "@/components/AudioButton";

const introAudioButton: ComponentProps<typeof AudioButton>[] = [
    { playbackRate: 0.9, audioTitle: "Intro - Steg 1", audioUrl: "https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/sign/audio%20files/KONSTEN_StegETT_SE.wav?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV81YmE4YmZlYy0wOTI0LTQ0YjctYmUzZi1kOTc0ZWYwYzk3NDciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyBmaWxlcy9LT05TVEVOX1N0ZWdFVFRfU0Uud2F2IiwiaWF0IjoxNzU4NzAyNzY4LCJleHAiOjMxNzA4NzE2Njc2OH0.Y0qgi2lVMpd0FxHf4jiKZlJK443bezU0gM6WZ2leex0", buttonText: "Intro", variant: "step-current" },
    { audioTitle: "Intro - Steg 2", audioUrl: "https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/sign/audio%20files/KONSTEN_StegTVA_SE.wav?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV81YmE4YmZlYy0wOTI0LTQ0YjctYmUzZi1kOTc0ZWYwYzk3NDciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyBmaWxlcy9LT05TVEVOX1N0ZWdUVkFfU0Uud2F2IiwiaWF0IjoxNzYyMDc2NjA4LCJleHAiOjMxNzA5MDU0MDYwOH0.gZuPHfwRjEJEnFfOC1L0gWVlaHCDrCkIWxp1cMs48b0", buttonText: "Intro", variant: "step-current" },
    { audioTitle: "Intro - Steg 3", audioUrl: "https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/sign/audio%20files/KONSTEN_StegTRE_SE.wav?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV81YmE4YmZlYy0wOTI0LTQ0YjctYmUzZi1kOTc0ZWYwYzk3NDciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyBmaWxlcy9LT05TVEVOX1N0ZWdUUkVfU0Uud2F2Iiwic2NvcGUiOiJkb3dubG9hZCIsImlhdCI6MTc4NjYxNTkwNywiZXhwIjozMzI5MTA3OTkwN30.j2fbV9tmGLMyk4NlCq4SNemba28PBiIuI5yZ8nDp2d4", buttonText: "Intro", variant: "step-3" },

];
const reflectAudioButton: ComponentProps<typeof AudioButton>[] = [
    { audioTitle: "Reflektion - Steg 1", audioUrl: "https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/sign/audio%20files/KONSTEN_StegETT_MEDITATION_SE.wav?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV81YmE4YmZlYy0wOTI0LTQ0YjctYmUzZi1kOTc0ZWYwYzk3NDciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyBmaWxlcy9LT05TVEVOX1N0ZWdFVFRfTUVESVRBVElPTl9TRS53YXYiLCJpYXQiOjE3NTg3MDI4MzMsImV4cCI6MzE3MDg3MTY2ODMzfQ.3IOb00ne_Yv6h4NFobuVy0-u2ATF63UJJhtJFSlaWnc", buttonText: "Reflektion", variant: "step-current" },
    { audioTitle: "Reflektion - Steg 2", audioUrl: "https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/sign/audio%20files/KONSTEN_StegTVA_MEDITATION_SE.wav?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV81YmE4YmZlYy0wOTI0LTQ0YjctYmUzZi1kOTc0ZWYwYzk3NDciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyBmaWxlcy9LT05TVEVOX1N0ZWdUVkFfTUVESVRBVElPTl9TRS53YXYiLCJpYXQiOjE3NjIwNzcxMTUsImV4cCI6MzE3MDkwNTQxMTE1fQ.4XXMelghzyq3Cm_e8-jlQNSmdU4Rm16UlPBDFmnCgVU", buttonText: "Reflektion", variant: "step-current" },
    { audioTitle: "Reflektion - Steg 3", audioUrl: "https://lmwvrcrdcptbpvotelvu.supabase.co/storage/v1/object/sign/audio%20files/KONSTEN_StegTRE_MEDITATION_SE.wav?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV81YmE4YmZlYy0wOTI0LTQ0YjctYmUzZi1kOTc0ZWYwYzk3NDciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJhdWRpbyBmaWxlcy9LT05TVEVOX1N0ZWdUUkVfTUVESVRBVElPTl9TRS53YXYiLCJzY29wZSI6ImRvd25sb2FkIiwiaWF0IjoxNzg2NjE1OTY1LCJleHAiOjMzMjkxMDc5OTY1fQ.Dpaaok5CoRT9K6XAM9VFa0igcRkPvOS5bW5g-uoEZfM", buttonText: "Reflektion", variant: "step-3" },
];

// Vertical padding of ChapterCard (its p-6), which the measured card height excludes
const CARD_PADDING_PX = 24;
// How far the arrows sit outside the current card's edge, so they land on the visible strip of the neighbouring stacked card
const ARROW_OUTSET_PX = 17;
// Arrow button: 24px icon (h-6 w-6) + 2px padding (p-0.5) + 1px border on each side
const ARROW_SIZE_PX = 24 + 2 * 2 + 2 * 1;
// Horizontal padding of the page container (its px-6)
const PAGE_GUTTER_PX = 24;
const ARROW_SCREEN_MARGIN_PX = 4;
// How far an arrow sticks out past the card edge, beyond what the page gutter already covers
const ARROW_RESERVE_PX = Math.max(0, ARROW_OUTSET_PX + ARROW_SIZE_PX / 2 + ARROW_SCREEN_MARGIN_PX - PAGE_GUTTER_PX);

const CARD_MAX_WIDTH_PX = 520;
// Percentages resolve against the stack container, which is as wide as the page content
const cardWidthCss = `min(${CARD_MAX_WIDTH_PX}px, 100% - ${2 * ARROW_RESERVE_PX}px)`;
const cardHalfWidthCss = `min(${CARD_MAX_WIDTH_PX / 2}px, 50% - ${ARROW_RESERVE_PX}px)`;

const StepSection = forwardRef<HTMLElement>(function StepSection(_, forwardedRef) {
    const thisRef = useRef<HTMLElement>(null);

    const [completedSteps, setCompletedSteps] = useState<StepId[]>([]);

    const [stepsByNumber, setStepsByNumber] = useState<Map<number, StepEntry>>(new Map());
    const [currentStepNumber, _setCurrentStepNumber] = useState(5);
    const setCurrentStepNumber = useCallback((value: React.SetStateAction<number>) => {
        _setCurrentStepNumber((prev) => {
            const v = typeof (value) === "number" ? value : value(prev);

            localStorage.setItem(
                currentStepLocalStorageKey,
                String(v)
            );

            return v;
        });
    }, []);
    const currentStep = stepsByNumber.get(currentStepNumber);

    const currentStepLocalStorageKey = "current-step";
    const currentChapterLocalStorageKey = useMemo(() => `step-${currentStep?.id}-chapter`, [currentStep?.id]);

    const cachedChaptersByStepId = useRef<Map<StepId, ChapterEntry[]>>(new Map());
    const cachedCompletedChaptersByStepId = useRef<Map<StepId, Set<ChapterId>>>(new Map());

    const [chapters, setChapters] = useState<ChapterEntry[]>([]);
    const [currentChapterIndex, _setCurrentChapterIndex] = useState<number>(0);
    const currentChapter = chapters?.[currentChapterIndex];
    const { user } = useUserContext();
    const chapterRefs = useRef<Map<ChapterId, HTMLElement>>(new Map());
    const setCurrentChapterIndex = useCallback((value: React.SetStateAction<number>) => {
        _setCurrentChapterIndex((prev) => {
            const v = typeof (value) === "number" ? value : value(prev);

            localStorage.setItem(
                currentChapterLocalStorageKey,
                String(v)
            );

            return v;
        });
    }, [currentChapterLocalStorageKey]);

    const [isStepDropdownOpen, setIsStepDropdownOpen] = useState<boolean>(false);

    const { journeySectionRef } = useNavigationContext();

    const fetchingStepsRef = useRef(false);


    useImperativeHandle(forwardedRef, () => thisRef.current as HTMLInputElement);
    const handleRef = useCallback((node: HTMLElement | null) => {
        thisRef.current = node;
        journeySectionRef.current = node;
    }, [journeySectionRef]);


    useEffect(() => {
        if (fetchingStepsRef.current) return;

        (async () => {
            fetchingStepsRef.current = true;
            const { data, error } = await supabase.from("steps_view").select("title, number, id, has_access, effective_tier");
            if (error) {
                console.error(error);
                return;
            }
            setStepsByNumber(new Map(data.map(row => [row.number, row])));

            setCurrentStepNumber(+(localStorage.getItem(currentStepLocalStorageKey) ?? 1));
            setCurrentChapterIndex(+(localStorage.getItem(currentChapterLocalStorageKey) ?? 0));
            fetchingStepsRef.current = false;
        })();
    }, [currentChapterLocalStorageKey, currentStepLocalStorageKey, setCurrentChapterIndex, setCurrentStepNumber]);

    useEffect(() => {
        (async () => {
            const { data, error } = await supabase.from("completed_steps").select("step_id").eq("user_id", user.id);
            if (error) {
                console.error(error);
                return;
            }
            setCompletedSteps(data.map(completion => completion.step_id));
        })();
    }, [user.id]);

    useEffect(() => {
        if (!currentStep) return;
        (async () => {
            // === Chapters ===
            let _chapters: ChapterEntry[];
            if (cachedChaptersByStepId.current.has(currentStep.id)) {
                _chapters = cachedChaptersByStepId.current.get(currentStep.id);
            } else {
                const { data: chaptersData, error: chaptersError } = await supabase
                    .from("chapters_view")
                    .select("id, title, number, next_chapter, prev_chapter, has_access, effective_tier")
                    .eq("step_id", currentStep.id)
                    .order("number", { ascending: true });

                if (chaptersError) {
                    console.error(chaptersError);
                    return;
                }

                if (!chaptersData) {
                    console.error("no chapters data returned");
                    return;
                }
                _chapters = chaptersData;
                cachedChaptersByStepId.current.set(currentStep.id, chaptersData);
            }
            setChapters(_chapters);
        })();
    }, [currentStep, user.id]);

    const appContentApi = useContext(AppContentAPIContext);

    // completed_steps is only fetched on mount, and the row itself is inserted by a
    // DB trigger, so mirror it locally once every chapter in the current step is
    // completed. Otherwise the next step stays locked until the page is reloaded.
    useEffect(() => {
        if (!appContentApi || !currentStep || chapters.length === 0) return;
        // chapters lags behind currentStep while the new step's chapters are being
        // fetched, so make sure they actually belong to the current step
        if (cachedChaptersByStepId.current.get(currentStep.id) !== chapters) return;

        const stepId = currentStep.id;
        function checkStepCompleted() {
            const allChaptersCompleted = chapters.every(chapter => appContentApi.getChapterSnapshot(chapter.id).completed);
            if (!allChaptersCompleted) return;
            setCompletedSteps(prev => prev.includes(stepId) ? prev : [...prev, stepId]);
        }

        checkStepCompleted();
        const unsubscribes = chapters.map(chapter => appContentApi.subscribeToChapter(chapter.id, checkStepCompleted));
        return () => unsubscribes.forEach(unsubscribe => unsubscribe());
    }, [appContentApi, chapters, currentStep]);

    useEffect(() => {
        for (let i = 1; i <= stepsByNumber.size; i++) {
            if (currentStepNumber !== i) document.documentElement.classList.remove(`step-${i}`);
            else document.documentElement.classList.add(`step-${currentStepNumber}`);
        }

    }, [currentStepNumber, stepsByNumber.size]);

    const { unsavedChanges } = useUnsavedChanges();
    const unsavedRef = useRef(unsavedChanges);
    useEffect(() => {
        unsavedRef.current = unsavedChanges;
    }, [unsavedChanges]);

    useEffect(() => {
        function beforeUnload(e: BeforeUnloadEvent) {
            if (unsavedRef.current) {
                e.preventDefault();
                // Included for legacy support, e.g. Chrome/Edge < 119
                e.returnValue = true;
            }
        }
        window.addEventListener("beforeunload", beforeUnload);
        return () => window.removeEventListener("beforeunload", beforeUnload);
    }, []);

    const stepCard = useMemo(() => (
        <DropdownMenu onOpenChange={setIsStepDropdownOpen} open={isStepDropdownOpen}>
            <DropdownMenuTrigger asChild>
                <Card className="bg-gradient-card border-primary/20 hover:-translate-y-2 group transition-all duration-500 ease-smooth cursor-pointer">
                    <CardContent className="p-6">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary mb-1">{currentStepNumber}/3</div>
                            <div className="text-sm text-muted-foreground flex flex-row">
                                Steg
                                {isStepDropdownOpen
                                    ? <ChevronUp className="p-1" />
                                    : <ChevronDown className="p-1" />
                                }
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>Steg</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup
                    value={currentStepNumber.toString()}
                    onValueChange={(strValue) => setCurrentStepNumber(+strValue)}
                >
                    {[...stepsByNumber.values()].sort((a, b) => a.number - b.number).map((step, index, steps) =>
                        <DropdownMenuRadioItem
                            key={step.number}
                            value={"" + step.number}
                            disabled={
                                !config.unlockAll &&
                                index > 0 && !completedSteps.includes(steps[index - 1].id)
                            }
                        >
                            {step.number}. {step.title}
                        </DropdownMenuRadioItem>
                    )}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    ), [completedSteps, currentStepNumber, isStepDropdownOpen, setCurrentStepNumber, stepsByNumber]);


    const handlerProviderValues: React.ComponentProps<typeof StepHandlerProvider>["value"] = useMemo(() => ({
        currentStep,
        chapterRefs,
        setStepNumber: setCurrentStepNumber,
        setChapterNumber(chapterNumber) {
            const next = chapters.findIndex(chapter => chapter.number === chapterNumber);
            setCurrentChapterIndex(next);
        },
    }), [chapters, currentStep, setCurrentChapterIndex, setCurrentStepNumber]);

    return (
        <section className={`py-16 bg-background`} ref={handleRef}>
            <StepHandlerProvider value={handlerProviderValues}>
                <div className="container mx-auto px-6">
                    <div className="flex justify-between items-center mb-12">
                        <div>
                            <div className="flex flex-row flex-wrap gap-4">
                                <h2 className="text-3xl font-bold mb-4 text-foreground">
                                    {`Steg ${currentStepNumber}: ${currentStep?.title}`}
                                </h2>
                                <AudioButton {...introAudioButton[currentStepNumber - 1]} />
                                <AudioButton {...reflectAudioButton[currentStepNumber - 1]} />
                            </div>
                            <p className="text-muted-foreground">
                                Här skapar du grunden för din livsstil genom tre viktiga kapitel
                            </p>
                        </div>

                        {stepCard}
                    </div>
                    <Step
                        chapterRefs={chapterRefs}
                        chapters={chapters}
                        currentChapter={currentChapter}
                        currentChapterIndex={currentChapterIndex}
                        setCurrentChapterIndex={setCurrentChapterIndex}
                    />

                </div>
            </StepHandlerProvider>
        </section>
    );
});
type StepProps = {
    chapters: ChapterEntry[];
    currentChapterIndex: number;
    setCurrentChapterIndex: (value: React.SetStateAction<number>) => void;
    currentChapter: ChapterEntry;
    chapterRefs: React.MutableRefObject<Map<string, HTMLElement>>;
};
function Step({ chapters, currentChapterIndex, setCurrentChapterIndex, currentChapter, chapterRefs }: StepProps) {
    const {
        registerMeasure: registerSyncedHeightMeasureElement,
        registerOuter: registerSyncedHeightOuterElement,
        height: cardHeight,
    } = useSyncHeight(chapters?.length ?? 0);

    const { dragX: cardsDragX, bind: cardsBind } = useSwipeIndex({
        index: currentChapterIndex,
        setIndex: setCurrentChapterIndex,
        count: chapters?.length ?? 0,
        thresholdPx: 70,
    });

    const atStart = currentChapter?.number === 1;
    const atEnd = currentChapter?.number === chapters?.length;
    const dampedDragX = (atStart && cardsDragX > 0) || (atEnd && cardsDragX < 0)
        ? cardsDragX * 0.35
        : cardsDragX;


    return <div
        className="relative w-full overflow-visible"
        {...cardsBind}
        style={{ height: cardHeight, ...cardsBind.style }}
    >
        {!chapters && (
            <div className="text-xl flex flex-row items-center">
                Hämtar steg
                <LoaderCircle className="animate-spin" />
            </div>
        )}
        {([-1, 1] as const).map(direction => {
            const targetIndex = currentChapterIndex + direction;
            if (!chapters || targetIndex < 0 || targetIndex >= chapters.length) return null;
            const Icon = direction < 0 ? ChevronLeft : ChevronRight;
            return (
                <button
                    key={direction}
                    type="button"
                    aria-label={direction < 0 ? "Previous chapter" : "Next chapter"}
                    className="absolute -translate-y-1/2 -translate-x-1/2 rounded-full bg-background/80 border border-border shadow-sm p-0.5 text-muted-foreground hover:text-foreground hover:bg-background transition-colors"
                    style={{
                        zIndex: chapters.length * 2 + 2,
                        left: `calc(50% + ${direction} * (${cardHalfWidthCss} + ${ARROW_OUTSET_PX}px))`,
                        top: cardHeight / 2 + CARD_PADDING_PX
                    }}
                    onClick={() => setCurrentChapterIndex(targetIndex)}
                >
                    <Icon className="h-6 w-6" />
                </button>
            );
        })}
        {chapters?.map((chapter, index) => {
            const diffFromCurrent = chapter.number - (currentChapter?.number ?? 1);
            const isCurrent = diffFromCurrent === 0;

            const xStep = 36; // px horizontal offset per step away from selected
            const yStep = 6;  // px vertical offset per step (optional "depth" feel)
            const scaleStep = 0.04; // scale reduction per step
            const absDiffFromCurrent = Math.abs(diffFromCurrent);


            let x = diffFromCurrent * xStep;
            if (isCurrent) x += dampedDragX;

            const y = absDiffFromCurrent * yStep;
            const scale = 1 - absDiffFromCurrent * scaleStep;

            // selected highest; others progressively lower. 
            // Use 2x the number of chapters so that they can be safely shifted in the next step
            let zIndex = (chapters.length - absDiffFromCurrent) * 2;
            // If swiping left, show next left cards on top of right cards and vice versa
            if ((dampedDragX < 0 && diffFromCurrent > 0) ||
                (dampedDragX > 0 && diffFromCurrent < 0)) {
                zIndex++;
            }

            const isDragging = dampedDragX !== 0 && isCurrent;

            return (
                <ChapterHandlerProvider
                    key={chapter.id}
                    chapter={chapter}
                >
                    <button
                        className={cn(
                            "absolute left-1/2 -translate-x-1/2 -translate-y-1/2 text-left",
                            !isDragging && "transition-[transform,box-shadow,scale] duration-300 ease-out",
                            isCurrent && "cursor-default"
                        )}
                        style={{
                            width: cardWidthCss,
                            zIndex,
                            transform: `translateX(-50%) translate3d(${x}px, ${y}px, 0) scale(${scale})`,
                        }}
                        onClick={() => {
                            if (isCurrent) return;
                            setCurrentChapterIndex(index);
                        }}
                    >
                        <ChapterCard
                            style={{
                                pointerEvents: isCurrent ? undefined : "none",
                            }}
                            ref={(element) => {
                                chapterRefs.current.set(chapter.id, element?.card);
                                registerSyncedHeightMeasureElement(index, element?.contentInnerWrapper);
                                registerSyncedHeightOuterElement(index, element?.content);
                            }}
                            chapter={chapter}
                        />
                    </button>
                </ChapterHandlerProvider>
            );
        })}
    </div>;
}

export default StepSection;